/**
 * TeamStrength Hub - Professional Team Grid Component
 * Full-width layout with collapsible domain groupings
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../components/ui/tooltip';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';

// Get contrasting text color for background
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#FFFFFF';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
};

// Get lighter version of color for backgrounds
const getLighterColor = (hexColor, opacity = 0.15) => {
  if (!hexColor) return '#F3F4F6';
  return `${hexColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

// Get heat style based on rank and domain color
const getHeatStyle = (rank, domainColor) => {
  if (!rank) return { backgroundColor: 'transparent', color: '#9CA3AF' };
  
  const intensity = rank <= 2 ? 0.9 : rank <= 5 ? 0.6 : rank <= 7 ? 0.35 : 0.15;
  
  return {
    backgroundColor: getLighterColor(domainColor, intensity),
    color: intensity > 0.5 ? getContrastColor(domainColor) : '#374151',
    fontWeight: rank <= 5 ? '700' : '500'
  };
};

const TeamGridPro = ({ gridData, members, model, onEditStrengths }) => {
  const [showFullNames, setShowFullNames] = useState(true);
  const [filterRange, setFilterRange] = useState('all');
  const [expandedDomains, setExpandedDomains] = useState({});

  // Group talents by category using model colors
  const categorizedData = useMemo(() => {
    if (!model?.categories || !model?.talents) return null;

    const categories = model.categories.map(cat => ({
      ...cat,
      color: cat.color || '#6B7280',
      label: cat.name?.charAt(0)?.toUpperCase() || '?',
      talents: model.talents.filter(t => t.category_id === cat.category_id)
    })).filter(cat => cat.talents.length > 0);

    return categories;
  }, [model]);

  // Toggle domain expansion
  const toggleDomain = (categoryId) => {
    setExpandedDomains(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Expand/collapse all
  const expandAll = () => {
    if (!categorizedData) return;
    const allExpanded = {};
    categorizedData.forEach(cat => {
      allExpanded[cat.category_id] = true;
    });
    setExpandedDomains(allExpanded);
  };

  const collapseAll = () => {
    setExpandedDomains({});
  };

  // Calculate team statistics
  const teamStats = useMemo(() => {
    if (!gridData?.rows || !categorizedData) return null;

    const domainCounts = {};
    const themeCounts = {};
    
    gridData.rows.forEach(row => {
      Object.entries(row.strengths || {}).forEach(([talentId, rank]) => {
        if (rank && rank <= 5) {
          for (const cat of categorizedData) {
            const talent = cat.talents.find(t => t.talent_id === talentId);
            if (talent) {
              domainCounts[cat.category_id] = {
                count: (domainCounts[cat.category_id]?.count || 0) + 1,
                name: cat.name,
                color: cat.color
              };
              themeCounts[talent.name] = {
                count: (themeCounts[talent.name]?.count || 0) + 1,
                categoryId: cat.category_id,
                color: cat.color
              };
              break;
            }
          }
        }
      });
    });

    const sortedDomains = Object.entries(domainCounts).sort((a, b) => b[1].count - a[1].count);
    const highDomain = sortedDomains[0];
    const lowDomain = sortedDomains[sortedDomains.length - 1];

    const topThemes = Object.entries(themeCounts)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([name, data], index) => ({
        rank: index + 1,
        name,
        count: data.count,
        color: data.color,
        categoryId: data.categoryId
      }));

    return { domainCounts, highDomain, lowDomain, topThemes };
  }, [gridData, categorizedData]);

  if (!gridData?.rows?.length || !categorizedData) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
            Noch keine Grid-Daten
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
            Füge Teammitglieder hinzu und weise ihnen Stärken zu.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Stats - Full Width */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-8">
              <div>
                <span className="text-slate-400 text-sm">TEAM MEMBERS</span>
                <p className="text-2xl font-bold">{members.length}</p>
              </div>
              {teamStats?.highDomain && (
                <div>
                  <span className="text-slate-400 text-sm">LEADING DOMAIN</span>
                  <div className="flex items-center gap-2 mt-1">
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold"
                      style={{ 
                        backgroundColor: teamStats.highDomain[1].color,
                        color: getContrastColor(teamStats.highDomain[1].color)
                      }}
                    >
                      {teamStats.highDomain[1].name?.charAt(0)}
                    </div>
                    <span className="font-semibold text-sm">
                      {teamStats.highDomain[1].name}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {teamStats?.topThemes && (
              <div>
                <span className="text-slate-400 text-sm">TOP FIVE THEMES</span>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {teamStats.topThemes.map((theme) => (
                    <Badge 
                      key={theme.name}
                      variant="outline"
                      className="text-xs border-opacity-50"
                      style={{ 
                        borderColor: theme.color,
                        color: '#FFFFFF',
                        backgroundColor: getLighterColor(theme.color, 0.3)
                      }}
                    >
                      {theme.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Grid - Full Width */}
      <Card>
        <CardContent className="p-4">
          <TooltipProvider>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th 
                      className="p-2 text-left font-medium text-slate-500 bg-slate-50 dark:bg-slate-800 sticky left-0 z-20 border-b border-slate-200 dark:border-slate-700"
                      style={{ minWidth: '150px' }}
                    >
                      Team Member
                    </th>
                    {categorizedData.map((cat) => (
                      <th
                        key={cat.category_id}
                        colSpan={expandedDomains[cat.category_id] ? cat.talents.length : 1}
                        className="p-0 border-b border-l border-slate-200 dark:border-slate-700"
                      >
                        <button
                          onClick={() => toggleDomain(cat.category_id)}
                          className="w-full p-2 font-bold text-center transition-colors hover:opacity-80 flex items-center justify-center gap-2"
                          style={{ 
                            backgroundColor: cat.color,
                            color: getContrastColor(cat.color)
                          }}
                        >
                          {expandedDomains[cat.category_id] ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="w-5 h-5 rounded bg-white/20 flex items-center justify-center text-xs font-bold">
                            {cat.label}
                          </span>
                          <span className="truncate">
                            {expandedDomains[cat.category_id] ? cat.name : cat.name.split(' ')[0]}
                          </span>
                          {!expandedDomains[cat.category_id] && (
                            <span className="text-xs opacity-70">({cat.talents.length})</span>
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                  {/* Talent Headers - only show when domain is expanded */}
                  <tr>
                    <th className="bg-slate-50 dark:bg-slate-800 sticky left-0 z-20 border-b border-slate-200 dark:border-slate-700" />
                    {categorizedData.map((cat) => 
                      expandedDomains[cat.category_id] ? (
                        cat.talents.map((talent) => (
                          <Tooltip key={talent.talent_id}>
                            <TooltipTrigger asChild>
                              <th
                                className="p-1 text-center font-medium border-b border-l border-slate-200 dark:border-slate-700 cursor-help"
                                style={{ 
                                  backgroundColor: getLighterColor(cat.color, 0.2),
                                  minWidth: showFullNames ? '70px' : '35px',
                                  maxWidth: '100px'
                                }}
                              >
                                <span className="block truncate text-slate-700 dark:text-slate-300 text-xs">
                                  {showFullNames ? talent.name : talent.name.substring(0, 3)}
                                </span>
                              </th>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="font-semibold">{talent.name}</p>
                              {talent.description && (
                                <p className="text-xs text-slate-400 max-w-[200px]">{talent.description}</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ))
                      ) : (
                        <th 
                          key={`${cat.category_id}-collapsed`}
                          className="p-1 border-b border-l border-slate-200 dark:border-slate-700"
                          style={{ backgroundColor: getLighterColor(cat.color, 0.1) }}
                        />
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {gridData.rows.map((row) => (
                    <tr 
                      key={row.user_id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td 
                        className="p-2 font-medium text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 sticky left-0 z-10 border-b border-r border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => {
                          const member = members.find(m => m.user_id === row.user_id);
                          if (member && onEditStrengths) onEditStrengths(member);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-medium text-sm">
                            {row.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="truncate">{row.name}</span>
                        </div>
                      </td>
                      {categorizedData.map((cat) => 
                        expandedDomains[cat.category_id] ? (
                          cat.talents.map((talent) => {
                            const rank = row.strengths?.[talent.talent_id];
                            
                            if (filterRange === '1-5' && (!rank || rank > 5)) {
                              return (
                                <td 
                                  key={talent.talent_id}
                                  className="p-1 text-center border-b border-l border-slate-200 dark:border-slate-700 text-slate-300"
                                >
                                  -
                                </td>
                              );
                            }
                            if (filterRange === '6-10' && (!rank || rank < 6 || rank > 10)) {
                              return (
                                <td 
                                  key={talent.talent_id}
                                  className="p-1 text-center border-b border-l border-slate-200 dark:border-slate-700 text-slate-300"
                                >
                                  -
                                </td>
                              );
                            }

                            const style = getHeatStyle(rank, cat.color);
                            
                            return (
                              <td 
                                key={talent.talent_id}
                                className="p-1 text-center border-b border-l border-slate-200 dark:border-slate-700 transition-colors"
                                style={style}
                              >
                                {rank || '-'}
                              </td>
                            );
                          })
                        ) : (
                          <td 
                            key={`${cat.category_id}-${row.user_id}-collapsed`}
                            className="p-1 text-center border-b border-l border-slate-200 dark:border-slate-700"
                            style={{ backgroundColor: getLighterColor(cat.color, 0.05) }}
                          >
                            {(() => {
                              const count = cat.talents.filter(t => {
                                const rank = row.strengths?.[t.talent_id];
                                return rank && rank <= 5;
                              }).length;
                              return count > 0 ? (
                                <span 
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                                  style={{ 
                                    backgroundColor: cat.color,
                                    color: getContrastColor(cat.color)
                                  }}
                                >
                                  {count}
                                </span>
                              ) : '-';
                            })()}
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Controls & Legend - Below Grid */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
        {/* Left: Domain Legend */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500 font-medium">Domänen:</span>
          {categorizedData.map((cat) => (
            <button
              key={cat.category_id}
              onClick={() => toggleDomain(cat.category_id)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
                expandedDomains[cat.category_id] 
                  ? 'bg-white dark:bg-slate-700 shadow-sm ring-1 ring-slate-200 dark:ring-slate-600' 
                  : 'hover:bg-white/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <div 
                className="w-4 h-4 rounded flex items-center justify-center text-[10px] font-bold"
                style={{ 
                  backgroundColor: cat.color,
                  color: getContrastColor(cat.color)
                }}
              >
                {cat.label}
              </div>
              <span className="text-slate-600 dark:text-slate-400">
                {cat.name.split(' ')[0]}
              </span>
              {expandedDomains[cat.category_id] && (
                <ChevronDown className="w-3 h-3 text-slate-400" />
              )}
            </button>
          ))}
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-4">
          {/* Filter Range */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Ranking:</span>
            <div className="flex gap-1">
              {['all', '1-5', '6-10'].map((range) => (
                <Button
                  key={range}
                  variant={filterRange === range ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs h-7 px-2"
                  onClick={() => setFilterRange(range)}
                >
                  {range === 'all' ? 'Alle' : range}
                </Button>
              ))}
            </div>
          </div>

          {/* Full Names Toggle */}
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={showFullNames}
              onChange={(e) => setShowFullNames(e.target.checked)}
              className="rounded border-slate-300 w-3.5 h-3.5"
            />
            Volle Namen
          </label>

          {/* Expand/Collapse All */}
          <div className="flex gap-1 border-l border-slate-300 dark:border-slate-600 pl-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={expandAll}
            >
              Alle öffnen
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={collapseAll}
            >
              Alle schließen
            </Button>
          </div>
        </div>
      </div>

      {/* Intensity Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 px-2">
        <span>Intensität:</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-violet-600" />
          <span>1-2</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-violet-400" />
          <span>3-5</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-violet-200" />
          <span>6-10</span>
        </div>
        <span className="text-slate-400 ml-2">|</span>
        <span className="text-slate-400">Klicke auf Domäne zum Auf-/Zuklappen</span>
      </div>
    </div>
  );
};

export default TeamGridPro;
